import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import {
  CONST_DISABLED_STAKEHOLDERS,
  SECTION_TYPE,
  STATUS_SECTIONS,
} from 'constants/materialAssessment';
import useErrorMessage from 'hooks/useErrorMessage';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks/useApp';
import { selectStakeholderVcTab } from 'store/slices/general/selectors';
import { setStakeholderVcTab } from 'store/slices/general/slice';
import {
  useGetMaterialityStakeholdersListStatusQuery,
  useGetSectionsMAQuery,
  useUpdateSectionMAMutation,
} from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useGetMaterialityStakeholdersQuery,
  useGetSingleMAInitiateQuery, useUpdateMaterialityStakeholdersMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { setMaterialId } from 'store/slices/materialAssessment/slice';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityAssessmentStakeholderView from './MaterialAssessmentStakeholderView';

import type { SectionMAStatusValue } from 'store/slices/layoutsMA/interfaces/ISectionMAType';
import type IMaterialStakeholderItem from 'store/slices/materialAssessment/interfaces/IMaterialStakeholderItem';

function MaterialityAssessmentStakeholder() {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const dispatch = useDispatch();
  const stakeholderVcTab = useAppSelector(selectStakeholderVcTab);
  const [selectedStakeholders, setSelectedStakeholders] = useState<IMaterialStakeholderItem[]>([]);
  const [openModalRefused, setOpenModalRefused] = useState(false);
  const [openModalDetailsRefusal, setOpenModalDetailsRefusal] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const previousStatusRef = useRef<SectionMAStatusValue | null>(null);
  const nextNavigationDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextNavigationDelayMs = 500;

  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId }, {
    refetchOnMountOrArgChange: true,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    data: stakeholdersData,
    isLoading: isStakeholdersLoading,
    refetch: refetchStakeholders,
    error: fetchError,
  } = useGetMaterialityStakeholdersQuery({ maId: recordId, vcmId: stakeholderVcTab || 0 }, {
    refetchOnMountOrArgChange: true,
    skip: stakeholderVcTab === null,
  });

  const { refetch: refetchSections } = useGetSectionsMAQuery({
    maId: recordId,
  });

  const { data: sectionsData, isLoading: isSectorSpecificLoading } = useGetMaterialityStakeholdersListStatusQuery({
    maId: recordId,
    type: SECTION_TYPE.STAKEHOLDER_IDENTIFICATION_VCM,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === null,
  });

  const [updateMaterialityStakeholders] = useUpdateMaterialityStakeholdersMutation();

  const [updateSection] = useUpdateSectionMAMutation();

  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (singleMA && singleMA?.data?.valueChainMaps && singleMA?.data?.valueChainMaps?.length > 0
      && stakeholderVcTab === null) {
      dispatch(setStakeholderVcTab(singleMA.data.valueChainMaps[0].id));
    }
  }, [singleMA, dispatch, stakeholderVcTab]);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  useEffect(() => {
    if (recordId) {
      dispatch(setMaterialId(Number(recordId)));
    }
  }, [recordId, dispatch]);

  useEffect(() => () => {
    if (!nextNavigationDelayRef.current) {
      return;
    }

    clearTimeout(nextNavigationDelayRef.current);
  }, []);

  const handleSortStakeholders = (
    stakeholders: IMaterialStakeholderItem[],
  ) => stakeholders.sort((a, b) => a.type.localeCompare(b.type));

  useEffect(() => {
    if (!stakeholdersData?.data?.items) {
      return;
    }

    const newArray = stakeholdersData.data.items.map((item) => {
      const findSection = sectionsData?.data?.subSections
        .find((section) => section.type.split('stakeholder_')[1] === item.type);
      return {
        ...item,
        status: findSection?.status || STATUS_SECTIONS.EMPTY,
      };
    });
    const sortedArray = handleSortStakeholders(newArray);
    setSelectedStakeholders(sortedArray);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholdersData]);

  useEffect(() => {
    if (!sectionsData?.data?.subSections) {
      return;
    }

    setSelectedStakeholders((prev) => {
      if (prev.length === 0) {
        return prev;
      }

      return prev.map((item) => {
        const findSection = sectionsData.data.subSections
          .find((section) => section.type.split('stakeholder_')[1] === item.type);
        const status = findSection?.status || STATUS_SECTIONS.EMPTY;

        if (item.status === status) {
          return item;
        }

        return { ...item, status };
      });
    });
  }, [sectionsData]);

  const onUpdateStakeholder = useCallback((id: string | null, comment?: string, active?: boolean) => {
    if (isViewOnly) {
      return;
    }
    const findItem = selectedStakeholders.find((item) => item.type === id);
    if (findItem) {
      const body = {
        type: findItem.type,
        isActive: active ? !findItem.isActive : findItem.isActive,
        deactivationReason: comment || findItem.deactivationReason,
      };
      updateMaterialityStakeholders({ maId: recordId, body, vcmId: stakeholderVcTab || 0 }).then(() => {
        refetchStakeholders();
        refetchSections();
      }).then(() => {
        toast.success(i18n.t('notifications.stakeholderUpdated'));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorStakeholderUpdate'));
      });
    }
  }, [
    selectedStakeholders,
    recordId,
    updateMaterialityStakeholders,
    refetchStakeholders,
    refetchSections,
    stakeholderVcTab,
    isViewOnly,
  ]);

  const onCheckStakeholder = (id: string) => {
    if (isViewOnly) {
      return;
    }
    setSelectedStakeholders((prev) => prev.map((stakeholder) => ({
      ...stakeholder,
      isActive: stakeholder.type === id ? !stakeholder.isActive : stakeholder.isActive,
    })));
    const index = selectedStakeholders.findIndex((stakeholder) => stakeholder.type === id);
    setCurrentId(id);
    if (index !== -1 && selectedStakeholders[index].isActive) {
      setOpenModalRefused(true);
    } else {
      onUpdateStakeholder(id, undefined, true);
    }
  };

  const onRejectModal = () => {
    setOpenModalRefused(false);
    setSelectedStakeholders((prev) => prev.map((stakeholder) => ({
      ...stakeholder,
      isActive: stakeholder.type === currentId ? !stakeholder.isActive : stakeholder.isActive,
    })));
  };

  const onSubmitModal = (comment: string) => {
    setOpenModalRefused(false);
    if (currentId) {
      const updatedStakeholders = selectedStakeholders.map((stakeholder) => {
        if (stakeholder.type === currentId) {
          return { ...stakeholder, deactivationReason: comment };
        }
        return stakeholder;
      });
      setSelectedStakeholders(updatedStakeholders);
      setOpenModalDetailsRefusal(true);
      onUpdateStakeholder(currentId, comment);
    }
  };

  const onCloseModalDetailsRefusal = () => {
    setOpenModalDetailsRefusal(false);
    setCurrentId(null);
  };

  const onHandleNext = () => {
    if (nextNavigationDelayRef.current) {
      clearTimeout(nextNavigationDelayRef.current);
    }

    nextNavigationDelayRef.current = setTimeout(() => {
      nextNavigationDelayRef.current = null;
      navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
    }, nextNavigationDelayMs);
  };

  const onPrevious = () => {
    navigate(AppRoutes.materialityAssessmentInitiate.replace(':recordId', recordId));
    dispatch(setStakeholderVcTab(null));
  };

  const onClickStakeholder = (id: string) => {
    navigate(`${AppRoutes.materialityAssessmentStakeholdersURL
      .replace(':recordId', recordId)}/${id}/${stakeholderVcTab}`);
  };

  const handleClickStakeholderTitle = (id: string) => {
    const stakeholder = selectedStakeholders.find((item) => item.type === id);

    if (!stakeholder || stakeholder.isActive || !stakeholder.deactivationReason) {
      return;
    }

    setCurrentId(id);
    setOpenModalDetailsRefusal(true);
  };

  const handleClickVcTab = (vcId: number) => {
    dispatch(setStakeholderVcTab(vcId));
    previousStatusRef.current = null;
  };

  useEffect(() => {
    if (isViewOnly) {
      return;
    }
    const checkActive = selectedStakeholders.filter(
      (stakeholder) => stakeholder.isActive && CONST_DISABLED_STAKEHOLDERS.includes(stakeholder.type),
    );

    if (checkActive.length > 0) {
      checkActive.forEach((item) => {
        const findItem = selectedStakeholders.find((item2) => item2.type === item.type);
        if (findItem) {
          const body = {
            type: findItem.type,
            isActive: !findItem.isActive,
            deactivationReason: 'Disabled',
          };
          updateMaterialityStakeholders({
            maId: recordId,
            body,
            vcmId: stakeholderVcTab || 0,
          }).then(() => { refetchStakeholders(); });
        }
      });
    }
  }, [
    selectedStakeholders,
    recordId,
    updateMaterialityStakeholders,
    refetchStakeholders,
    stakeholderVcTab,
    isViewOnly,
  ]);

  useEffect(() => {
    if (isViewOnly) {
      return;
    }
    if (sectionsData && sectionsData.data && stakeholderVcTab) {
      let hasCompleted: SectionMAStatusValue = STATUS_SECTIONS.EMPTY;
      const activeSections = sectionsData?.data?.subSections.filter((subSection) => subSection.isActive);
      const allCompleted = activeSections.every(
        (subSection) => subSection.status === STATUS_SECTIONS.EMPTY,
      );
      if (allCompleted) {
        hasCompleted = STATUS_SECTIONS.IN_PROGRESS;
      }

      if (previousStatusRef.current !== hasCompleted
        && sectionsData.data.status === STATUS_SECTIONS.EMPTY) {
        previousStatusRef.current = hasCompleted;
        updateSection({
          maId: recordId,
          type: SECTION_TYPE.STAKEHOLDER_IDENTIFICATION_VCM,
          valueChainMapId: stakeholderVcTab,
          data: {
            isActive: true,
            status: STATUS_SECTIONS.IN_PROGRESS,
          },
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsData, stakeholderVcTab, recordId, isViewOnly]);

  const loading = isStakeholdersLoading || isUserLoading || isSectorSpecificLoading
  || isLoadingSingleMA;

  return (
    <MaterialityAssessmentStakeholderView
      selectedStakeholders={selectedStakeholders}
      isUserLoading={loading}
      onCheckStakeholder={onCheckStakeholder}
      onHandleNext={onHandleNext}
      openModalRefused={openModalRefused}
      onRejectModal={onRejectModal}
      onSubmitModal={onSubmitModal}
      openModalDetailsRefusal={openModalDetailsRefusal}
      onCloseModalDetailsRefusal={onCloseModalDetailsRefusal}
      userData={userData?.data}
      currentId={currentId}
      onClickStakeholder={onClickStakeholder}
      onClickStakeholderTitle={handleClickStakeholderTitle}
      onPrevious={onPrevious}
      mainVcTabs={singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ value: itemVC.name, id: itemVC.id, name: itemVC.name }),
      ) || []}
      handleClickVcTab={handleClickVcTab}
      activeVcTab={stakeholderVcTab || 0}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityAssessmentStakeholder;
